import { useCallback } from 'react'

import { UPLOAD_INPUT_FILE_ERROR_MAPS } from './upload-input-file.consts'
import type { IUploadInputFileError, TUploadInputFileModel } from './upload-input-file.types'

export const useUploadInputFileModel: TUploadInputFileModel = (onUpload, onError, config) => {
  const validateFiles = useCallback(
    (files: FileList | null): { isValid: boolean; error?: IUploadInputFileError } => {
      if (!files || files.length === 0) {
        return { isValid: false }
      }

      if (files.length > config.permissions.maxFilesPerDrop) {
        return {
          isValid: false,
          error: UPLOAD_INPUT_FILE_ERROR_MAPS.maxFiles,
        }
      }

      const filesArray = Array.from(files)
      const hasInvalidExtension = filesArray.some(file => {
        // Extract file extension safely
        const lastDotIndex = file.name.lastIndexOf('.')

        // Invalid if there is no dot, the dot is the first character (e.g. ".env"),
        // or the dot is the last character (e.g. "file.")
        if (lastDotIndex <= 0 || lastDotIndex === file.name.length - 1) {
          return true
        }

        const fileExtension = file.name.substring(lastDotIndex).toLowerCase()
        const normalizedAllowedExtensions = config.permissions.fileExtensions.map(ext =>
          ext.toLowerCase().startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`
        )

        return !normalizedAllowedExtensions.includes(fileExtension)
      })

      if (hasInvalidExtension) {
        return {
          isValid: false,
          error: UPLOAD_INPUT_FILE_ERROR_MAPS.fileExtension,
        }
      }

      return { isValid: true }
    },
    [config.permissions]
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      const validation = validateFiles(files)

      if (!validation.isValid) {
        if (validation.error && onError) {
          onError(validation.error)
        }

        event.target.value = ''

        return
      }

      if (files) {
        const filesArray = Array.from(files)

        onUpload(filesArray)

        event.target.value = ''
      }
    },
    [validateFiles, onUpload, onError]
  )

  const acceptAttribute = config.permissions.fileExtensions.join(',')
  const isMultiple = config.permissions.maxFilesPerDrop > 1

  return {
    handleChange,
    acceptAttribute,
    isMultiple,
  }
}
